import React, { useContext, useEffect } from 'react';

import { Accordion, AccordionSummary, AccordionDetails, Tooltip, Grid } from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { MenuContext } from '../../context/Menu';
import { MenuItemType } from './Type';
import MenuRoutes from './MenuList';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import OrganizationContainer from './OrgInfo';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { hasAdminAccess, userLogin } from '@Services/hasAdminAccess';
import { CheckCondition, isRouteAllowed } from '../../utils/allowedActions';
import UserMenuRoutes from './UserMenuList';

// --------------------
// Styled Components
// --------------------

const MenuContainer = styled(Grid)(() => ({
  height: 'calc(100vh - 164px)',
  overflowY: 'auto',
  overflowX: 'hidden',

  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(0, 0, 0, 0.3)',
  },
}));

const MenuContentWrapper = styled('div')(() => ({
  padding: '16px',
  paddingTop: '8px',
  width: '100%',
}));

const ListItem = styled('div')<{ isMenuOpen?: boolean }>(({ theme, isMenuOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: isMenuOpen ? '12px 16px' : '12px',
  borderRadius: '8px',
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  },
}));

const MenuItemWrapper = styled(Accordion)(() => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  margin: '0 !important',
  borderRadius: '8px',
  overflow: 'hidden',

  '&:before': {
    display: 'none',
  },

  '&.Mui-expanded': {
    margin: '0 !important',
  },
}));

const MenuItemLabel = styled('span')(({ theme }) => ({
  marginLeft: '16px',
  fontWeight: 500,
  fontSize: '14px',
  color: theme.palette.text.primary,
  flex: 1,
}));

const NestedMenuItem = styled('li')(({ theme }) => ({
  width: '100%',
  height: '40px',
  padding: '10px 16px 10px 56px',
  cursor: 'pointer',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s ease',
  margin: '2px 0',

  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.06),
  },

  '&[data-active="true"]': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    fontWeight: 500,
    color: theme.palette.secondary.main,
  },
}));

const CompactIconContainer = styled('div')(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  color: theme.palette.secondary.main,
  cursor: 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    transform: 'scale(1.05)',
  },
}));

// --------------------
// Menu Item Component
// --------------------

function MenuItem(props: MenuItemType) {
  const { isMenuOpen, Icon, value, nestedPaths } = props;
  const location = useLocation();
  const theme = useTheme();

  const loginUser = useSelector((state: RootStateType) => state.loginUser);
  const isPrepaid = loginUser?.profile?._accountId?.isPrepaid;
  const isAdmin = !userLogin();

  const hideWallet = !(!isAdmin && isPrepaid);
  const hideCredit = !(!isAdmin && !isPrepaid);

  const isActive = nestedPaths?.some(
    path => location.pathname === path.pathURL || location.pathname.startsWith(path.pathURL + '/'),
  );

  const filteredNestedPaths = nestedPaths?.filter(pathObj => {
    if (
      (hideWallet && ['/transactions/wallet'].includes(pathObj.pathURL)) ||
      (hideCredit && ['/transactions/credit'].includes(pathObj.pathURL)) ||
      (!isAdmin && ['/transactions/credits-and-wallets', '/transactions/list'].includes(pathObj.pathURL))
    ) {
      return false;
    }

    if (isAdmin && ['/my-rates'].includes(pathObj.pathURL)) {
      return false;
    }

    return true;
  });

  // -------- Compact Menu --------
  if (!isMenuOpen) {
    return (
      <Tooltip title={value} placement="right" arrow>
        <div style={{ margin: '4px 0' }}>
          <CompactIconContainer>
            <Icon style={{ width: '20px', height: '20px' }} />
          </CompactIconContainer>
        </div>
      </Tooltip>
    );
  }

  // -------- Expanded Menu --------
  return (
    <MenuItemWrapper>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        sx={{
          padding: 0,
          margin: 0,
          minHeight: '48px',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
          },
        }}
      >
        <ListItem
          isMenuOpen={isMenuOpen}
          style={{
            backgroundColor: isActive ? alpha(theme.palette.secondary.main, 0.12) : 'transparent',
            flex: 1,
          }}
        >
          <Icon
            style={{
              width: '20px',
              height: '20px',
              color: isActive ? theme.palette.secondary.main : 'inherit',
            }}
          />

          <MenuItemLabel
            style={{
              color: isActive ? theme.palette.secondary.main : 'inherit',
            }}
          >
            {value}
          </MenuItemLabel>
        </ListItem>
      </AccordionSummary>

      <AccordionDetails sx={{ padding: '8px 0' }}>
        <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
          {filteredNestedPaths?.map((pathObj, index) => {
            const isNestedActive =
              location.pathname === pathObj.pathURL || location.pathname.startsWith(pathObj.pathURL + '/');

            return (
              <Link
                key={`${pathObj.pathURL}-${index}`}
                to={pathObj.pathURL}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <NestedMenuItem data-active={isNestedActive}>
                  <KeyboardArrowRightIcon
                    sx={{
                      fontSize: 16,
                      mr: 1,
                      opacity: 0.6,
                      color: isNestedActive ? theme.palette.secondary.main : 'inherit',
                    }}
                  />
                  {pathObj.label}
                </NestedMenuItem>
              </Link>
            );
          })}
        </ul>
      </AccordionDetails>
    </MenuItemWrapper>
  );
}

// --------------------
// Main Menu Component
// --------------------

function Menu() {
  const { isMenuOpen, menuWidth } = useContext(MenuContext);

  const { PagesData, accessGroupById, SideNavbarPagesIds, SideNavbarActionsIds } = useSelector(
    (state: RootStateType) => state.accessGroup,
  );

  const { profile } = useSelector((state: RootStateType) => state.loginUser);

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationData);

  const { name = '' } = organizationData || {};

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isUserLogin = userLogin();
  const isAdmin = hasAdminAccess();

  const filteredPagesData = PagesData.filter(page => SideNavbarPagesIds.includes(page._id));

  useEffect(() => {
    const check = CheckCondition(pathname);

    if (profile?.accessGroup === 'pu_all') {
      let matchFound = false;

      for (const menuRoute of UserMenuRoutes) {
        for (const nestedPath of menuRoute.nestedPaths) {
          if (pathname.toLowerCase().startsWith(nestedPath.pathURL.toLowerCase())) {
            matchFound = true;
            break;
          }
        }
        if (matchFound) break;
      }

      if (!matchFound && pathname !== '/') {
        navigate('/');
      }
    } else {
      if (check) {
        const actions = accessGroupById?.actions || [];

        if (actions.length > 0) {
          const res = isRouteAllowed(pathname, actions);
          if (!res) navigate(-1);
        }
      }
    }
  }, [accessGroupById?.pagesId, PagesData]);

  return (
    <MenuContainer container>
      <MenuContentWrapper>
        {!isUserLogin ? (
          <>
            {isAdmin ? (
              <>
                {MenuRoutes.filter(menu => {
                  if (name === 'Cargo Solutions Network' && menu.label === 'Squad') {
                    return false;
                  }
                  return true;
                }).map(menu => (
                  <MenuItem
                    key={menu.label}
                    isMenuOpen={isMenuOpen}
                    menuWidth={menuWidth || 308}
                    value={menu.label}
                    Icon={menu.Icon}
                    nestedPaths={menu.nestedPaths}
                  />
                ))}
              </>
            ) : (
              <>
                {MenuRoutes.map(menu => {
                  const filteredNestedPaths = menu.nestedPaths?.filter(nestedPath => {
                    if (menu.label === 'Invoice') {
                      return filteredPagesData.some(page =>
                        page.name.toLowerCase().startsWith(nestedPath.matchActionName.toLowerCase()),
                      );
                    }

                    return (
                      filteredPagesData.some(page => page.name === nestedPath.matchActionName) ||
                      SideNavbarActionsIds.some(action =>
                        action.details?.name?.includes(nestedPath.matchActionName.toLowerCase()),
                      )
                    );
                  });

                  if (filteredNestedPaths?.length) {
                    return (
                      <MenuItem
                        key={menu.label}
                        isMenuOpen={isMenuOpen}
                        menuWidth={menuWidth || 308}
                        value={menu.label}
                        Icon={menu.Icon}
                        nestedPaths={filteredNestedPaths}
                      />
                    );
                  }

                  return null;
                })}
              </>
            )}
          </>
        ) : (
          <>
            {UserMenuRoutes.map(menu => {
              const filteredNestedPaths = menu.nestedPaths.filter(
                nestedPath => !(name === 'Cargo Solutions Network' && nestedPath.pathURL === '/my-rates'),
              );

              return (
                <MenuItem
                  key={menu.label}
                  isMenuOpen={isMenuOpen}
                  menuWidth={menuWidth || 308}
                  value={menu.label}
                  Icon={menu.Icon}
                  nestedPaths={filteredNestedPaths}
                />
              );
            })}
          </>
        )}
      </MenuContentWrapper>

      {isMenuOpen && <OrganizationContainer />}
    </MenuContainer>
  );
}

export default Menu;
